/**
 * Full-timeline preview: every scene, animation and transition, played
 * through the SAME frame compositor the export uses, so what you see here is
 * the frame the file will contain at that timestamp.
 *
 * Renders offscreen copies of the pages, never the live editor canvas, so it
 * needs none of the live preview's restore machinery.
 */

import { useEffect, useRef, useState } from 'react';
import type { PlaybackState, ProjectPage, VideoProject } from '@canvas/shared-types';
import { describeExportError } from '@/lib/video/exportErrors';
import { evenDim } from '@/lib/video/encoderCapabilities';
import { createFrameCompositor, type FrameCompositor } from '@/lib/video/frameCompositor';
import { createPlaybackClock, type PlaybackClock } from '@/lib/video/playbackClock';
import { createTimelineAudioPreview, type TimelineAudioPreview } from '@/lib/video/timelineAudioPreview';
import { previewAudioTracks } from '@/lib/video/timelineEdit';
import { formatTimecode } from '@/lib/video/sceneTimeline';

/** Preview resolution cap; the overlay is a monitor, not the export. */
const PREVIEW_MAX_SIDE = 960;

interface ScenePreviewOverlayProps {
  open: boolean;
  pages: ProjectPage[];
  video: VideoProject;
  /** Output size of the export, which the preview matches in aspect. */
  size: { width: number; height: number };
  onClose: () => void;
}

export default function ScenePreviewOverlay({ open, pages, video, size, onClose }: ScenePreviewOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const clockRef = useRef<PlaybackClock | null>(null);
  const requestRef = useRef<(ms: number) => void>(() => undefined);
  const [timeMs, setTimeMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [playback, setPlayback] = useState<PlaybackState>('stopped');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scale = Math.min(1, PREVIEW_MAX_SIDE / Math.max(size.width, size.height));
    canvas.width = evenDim(size.width * scale);
    canvas.height = evenDim(size.height * scale);

    let cancelled = false;
    let compositor: FrameCompositor | null = null;
    const players: TimelineAudioPreview[] = previewAudioTracks(video).map((track) => {
      const player = createTimelineAudioPreview();
      player.setTrack(track);
      return player;
    });
    const syncAudio = (ms: number) => {
      const state = {
        playing: clockRef.current?.getState() === 'playing',
        timelineMs: ms,
        totalMs: video.totalDurationMs,
        muted: false,
      };
      players.forEach((player) => player.update(state));
    };
    let rendering = false;
    let pendingMs: number | null = null;

    // Renders are async; drop intermediate times rather than queueing them.
    const pump = async () => {
      if (rendering || !compositor) return;
      rendering = true;
      try {
        while (pendingMs !== null && !cancelled) {
          const t = pendingMs;
          pendingMs = null;
          await compositor.renderFrameAt(t);
        }
      } catch (err) {
        if (!cancelled) {
          setError(describeExportError(err));
          clockRef.current?.pause();
        }
      } finally {
        rendering = false;
      }
    };
    requestRef.current = (ms) => {
      pendingMs = ms;
      setTimeMs(ms);
      syncAudio(ms);
      void pump();
    };

    setLoading(true);
    setError(null);
    void createFrameCompositor({ pages, video, target: canvas })
      .then((c) => {
        if (cancelled) {
          c.dispose();
          return;
        }
        compositor = c;
        setDurationMs(c.durationMs);
        const clock = createPlaybackClock({
          durationMs: c.durationMs,
          onTick: (ms) => requestRef.current(ms),
          onStateChange: (s) => {
            setPlayback(s);
            syncAudio(clock.getTimeMs());
          },
        });
        clockRef.current = clock;
        setLoading(false);
        requestRef.current(0);
        clock.play();
      })
      .catch((err) => {
        if (!cancelled) setError(describeExportError(err));
      });

    return () => {
      cancelled = true;
      clockRef.current?.destroy();
      clockRef.current = null;
      compositor?.dispose();
      players.forEach((player) => player.destroy());
      requestRef.current = () => undefined;
    };
  }, [open, pages, video, size.width, size.height]);

  // Capture phase + stopImmediatePropagation: keys here must not reach the
  // editor (Space would start the live scene preview behind the overlay).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      e.stopImmediatePropagation();
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.code === 'Space') {
        e.preventDefault();
        const clock = clockRef.current;
        if (!clock || e.repeat) return;
        if (clock.getState() === 'playing') clock.pause();
        else clock.play();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  const playing = playback === 'playing';
  const togglePlay = () => {
    const clock = clockRef.current;
    if (!clock) return;
    if (playing) clock.pause();
    else clock.play();
  };

  return (
    // Clicks must not reach the export dialog's backdrop, which closes on click.
    <div
      className="vt-preview-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Video preview"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="vt-preview-stage">
        <canvas ref={canvasRef} className="vt-preview-canvas" />
        {loading && !error && <p className="vt-preview-status">Loading scenes…</p>}
        {error && <p className="vt-preview-status ps-error">{error}</p>}
      </div>
      <div className="vt-preview-controls">
        <button type="button" className="vt-btn" onClick={togglePlay} disabled={loading}>
          {playing ? 'Pause' : 'Play'}
        </button>
        <input
          type="range"
          className="vt-preview-scrub"
          min={0}
          max={Math.max(1, durationMs)}
          step={1}
          value={Math.min(timeMs, durationMs)}
          aria-label="Preview position"
          disabled={loading}
          onChange={(e) => {
            const ms = Number(e.target.value);
            clockRef.current?.pause();
            clockRef.current?.seek(ms);
          }}
        />
        <span className="vt-timecode">
          {formatTimecode(timeMs)} / {formatTimecode(durationMs)}
        </span>
        <button type="button" className="vt-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
